import React, { useRef } from 'react';

import { Box, Divider } from '@mantine/core';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekday from 'dayjs/plugin/weekday';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

import formatDate from '@/utils/formatDate';

import { Address } from '@/types/organization';
import { GetPatientSchedulesResponse } from '@/types/patient';
import CustomModal from '@/ui/Modal';
import TextWrapper from '@/ui/TextWrapper';

dayjs.extend(weekday);
dayjs.extend(localizedFormat);

interface PrintScheduleIndexProps {
  patientName: string;
  address: Address;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  printData: GetPatientSchedulesResponse | undefined;
  isLoading?: boolean;
}

const PrintScheduleContent: React.FC<
  Omit<PrintScheduleIndexProps, 'modalOpen' | 'setModalOpen'>
> = ({ patientName, address, printData, isLoading }) => {
  const scheduleData = (printData?.data ?? [])
    .filter((schedule) => schedule.type === 'Routine')
    .slice(0, 3);

  if (isLoading) {
    return <TextWrapper text="Loading schedule data..." />;
  }
  if (scheduleData.length === 0) {
    return <TextWrapper text="No schedule data available." />;
  }
  return (
    <div
      id="print-schedule-content"
      style={{
        background: '#fff',
        minHeight: 600,
        padding: '10px 32px 10px 32px',
        fontFamily: 'Lato, Arial, sans-serif',
        color: '#213147',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
      <Box style={{ textAlign: 'center' }}>
        <TextWrapper fw={700} fz={20} mb={30} text="Tri City Cardiology Device Department" />
      </Box>
      <Divider my="xs" />
      <TextWrapper size="sm" mt={15} mb={30} text={formatDate(new Date(), 'dddd, MMMM D, YYYY')} />

      {address.line1 && (
        <>
          <TextWrapper
            size="sm"
            mt={15}
            mb={0}
            text={`${address?.line1 ?? ''} ${address?.line2 ?? ''}`}
          />
          <TextWrapper
            size="sm"
            mb={30}
            text={`${address?.city ?? ''}, ${address?.state ?? ''} ${address?.postalCode ?? ''}`}
          />
        </>
      )}

      <TextWrapper size="sm" text={`${patientName},`} mb={20} />
      <Box style={{ marginBottom: 16 }}>
        <TextWrapper mb={10} fz={14} text="Below is a schedule of your device check dates." />
        <TextWrapper
          mb={10}
          fz={14}
          text="If for any reason you are unable to keep any scheduled dates, please call us in advance to let us know. Please do not hesitate to contact us with any questions."
        />
        <TextWrapper
          mb={20}
          fz={14}
          text="Remote device check(s) will occur automatically at night. Make sure your home monitor is turned on."
        />
      </Box>

      <TextWrapper size="sm" fw={600} text="Remote Device Check Schedule:" />

      {scheduleData.map((data, idx) => (
        <TextWrapper
          size="sm"
          fw={600}
          ml={25}
          key={idx}
          text={formatDate(data.scheduledDate, 'dddd, MMMM D, YYYY')}
        />
      ))}
      <Box style={{ marginBottom: 32, marginTop: 40 }}>
        <TextWrapper size="sm" mb={10} fz={15} text="Sincerely," />
        <TextWrapper size="sm" fz={15} text="Device Department" />
      </Box>
    </div>
  );
};

const PrintScheduleIndex: React.FC<PrintScheduleIndexProps> = (props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scheduleData = (props.printData?.data ?? [])
    .filter((schedule) => schedule.type === 'Routine')
    .slice(0, 3);

  // Download as real PDF using pdf-lib
  const handleDownloadPDF = async () => {
    const formattedDate = formatDate(new Date(), 'dddd, MMMM D, YYYY');
    const scheduleDates = scheduleData.map((d) =>
      formatDate(d.scheduledDate, 'dddd, MMMM D, YYYY')
    );
    const address = props.address;

    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595.28, 841.89]); // A4
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const textColor = rgb(0.13, 0.19, 0.28);
    const leftMargin = 60;
    let y = 780;

    // Header (20px bold, centered)
    const pageWidth = 595.28;
    const headingText = 'Tri City Cardiology Device Department';
    const headingFontSize = 20;
    const headingWidth = boldFont.widthOfTextAtSize(headingText, headingFontSize);
    const headingX = (pageWidth - headingWidth) / 2;
    page.drawText(headingText, {
      x: headingX,
      y,
      size: headingFontSize,
      font: boldFont,
      color: textColor
    });

    y -= 18;
    // Divider
    page.drawLine({
      start: { x: leftMargin, y },
      end: { x: 535, y },
      thickness: 1,
      color: rgb(0.8, 0.8, 0.8)
    });

    // Date (14px)
    y -= 32;
    page.drawText(formattedDate, {
      x: leftMargin,
      y,
      size: 12,
      font,
      color: textColor
    });

    // Address (14px, two lines, no extra space, only if line1 is present)
    if (address?.line1) {
      y -= 28;
      page.drawText(`${address.line1 ?? ''} ${address.line2 ?? ''}`.trim(), {
        x: leftMargin,
        y,
        size: 12,
        font,
        color: textColor
      });
      y -= 16;
      page.drawText(
        `${address.city ?? ''}, ${address.state ?? ''} ${address.postalCode ?? ''}`.trim(),
        {
          x: leftMargin,
          y,
          size: 12,
          font,
          color: textColor
        }
      );
    }

    // Patient name (12px)
    y -= 32;
    page.drawText(`${props.patientName},`, {
      x: leftMargin,
      y,
      size: 12,
      font,
      color: textColor
    });

    // Body paragraphs (12px)
    const paragraphs = [
      'Below is a schedule of your device check dates.',
      'If for any reason you are unable to keep any scheduled dates, please call us in advance to let us know. Please do not hesitate to contact us with any questions.',
      'Remote device check(s) will occur automatically at night. Make sure your home monitor is turned on.'
    ];

    const wrapText = (text: string, maxWidth: number, fontSize: number) => {
      const words = text.split(' ');
      const lines: string[] = [];
      let line = '';
      for (const word of words) {
        const width = font.widthOfTextAtSize(line + word, fontSize);
        if (width > maxWidth) {
          lines.push(line.trim());
          line = word + ' ';
        } else {
          line += word + ' ';
        }
      }
      lines.push(line.trim());
      return lines;
    };

    const maxWidth = 470;
    for (const paragraph of paragraphs) {
      const lines = wrapText(paragraph, maxWidth, 12);
      for (const line of lines) {
        y -= 22;
        page.drawText(line, { x: leftMargin, y, size: 12, font, color: textColor });
      }
      y -= 8;
    }

    // Schedule Header (12px bold)
    y -= 22;
    page.drawText('Remote Device Check Schedule:', {
      x: leftMargin,
      y,
      size: 12,
      font: boldFont,
      color: textColor
    });

    // Schedule Dates (12px bold)
    for (const date of scheduleDates) {
      y -= 22;
      page.drawText(date, {
        x: leftMargin + 20,
        y,
        size: 12,
        font: boldFont,
        color: textColor
      });
    }

    // Footer (12px)
    y -= 40;
    page.drawText('Sincerely,', {
      x: leftMargin,
      y,
      size: 12,
      font,
      color: textColor
    });

    y -= 20;
    page.drawText('Device Department', {
      x: leftMargin,
      y,
      size: 12,
      font,
      color: textColor
    });

    // Save and download
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes as unknown as ArrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formattedDate}_${props.patientName}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <CustomModal
      modalProps={{
        opened: props.modalOpen,
        onClose: () => props.setModalOpen(false),
        size: 'lg',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{
        title: 'Preview Schedule'
      }}
      primaryButton={{
        text: 'Download',
        onClick: handleDownloadPDF,
        isDisabled: scheduleData.length === 0
      }}>
      <div ref={contentRef}>
        <PrintScheduleContent {...props} />
      </div>
    </CustomModal>
  );
};

export default PrintScheduleIndex;
